import { useEffect, useState } from "react";

// Third Party
import {
  createFileRoute,
  useNavigate,
  type NavigateOptions,
} from "@tanstack/react-router";
// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

import {
  useQuery,
  useQueries,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

// UI
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import TableSkeleton from "@/components/skeleton/TableSkeleton";
import Pagination from "@/components/Pagination";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogDescription,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import FileHandleViewer from "@/components/FileHandleViewer";
// Icons
import { PlusCircle, Save } from "lucide-react";

// Services
import { getTenureStatus, getTenureLength } from "@/services/master";
import { getCompanyDirectors, updateCompanyDirector } from "@/services/soe";
import type { CompanyDirector } from "@/types/soe";
import { S3_BUCKET_LINK } from "@/components/constants";
import formatDate from "@/utils/formatDate";
import { FileUpload } from "@/components/FileUpload";
import { clearLocalStorage } from "@/helpers/accessLocalStorage";
import { ReasonModal } from "@/components/ReasonModal";

export const Route = createFileRoute(
  "/_layout-private/account-management/soe/_information/director/"
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      activeStatus: search.activeStatus as string | undefined,
      page: Number(search.page) || 1,
    };
  },
});

function RouteComponent() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // const params = Route.useParams();
  const search = Route.useSearch();
  const id = localStorage.getItem("companyId") ?? "";
  const companyId = id.replace(/^"(.+)"$/, "$1");
  const { company_management } = useAccessPermission();

  const [selectedItem, setSelectedItem] = useState({} as CompanyDirector);
  const [editFormData, setEditFormData] = useState({
    status: "",
    endDoc: "",
  });

  const [editTenureModal, setEditTenureModal] = useState(false);
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");

  // Query
  const {
    data: tenureStatusRes,
    isLoading: tenureStatusLoading,
    error: ErrorTenureStatusRes,
  } = useQuery({
    queryKey: ["tenure-status"],
    queryFn: () => getTenureStatus(),
  });

  const [
    {
      data: directorsRes,
      isLoading: directorsLoading,
      error: ErrorDirectorsRes,
    },
    {
      data: tenureLengthRes,
      isLoading: tenureLengthLoading,
      error: ErrorTenureLengthRes,
    },
  ] = useQueries({
    queries: [
      {
        queryKey: ["directors", companyId, search.activeStatus, search.page],
        queryFn: () =>
          getCompanyDirectors({
            companyId: companyId,
            skip: 0,
            limit: 40,
            tenureStatusId: search.activeStatus as string,
          }),
        enabled: !!search.activeStatus,
      },
      {
        queryKey: ["tenure-length"],
        queryFn: () => getTenureLength(),
      },
    ],
  });

  if (ErrorDirectorsRes || ErrorTenureLengthRes || ErrorTenureStatusRes) {
    const isAxiosError = (
      error: unknown
    ): error is { response?: { data?: { message?: string } } } => {
      return typeof error === "object" && error !== null && "response" in error;
    };

    if (
      (isAxiosError(ErrorDirectorsRes) &&
        ErrorDirectorsRes?.response?.data?.message === "LOGGED_OUT") ||
      (isAxiosError(ErrorTenureLengthRes) &&
        ErrorTenureLengthRes?.response?.data?.message === "LOGGED_OUT") ||
      (isAxiosError(ErrorTenureStatusRes) &&
        ErrorTenureStatusRes?.response?.data?.message === "LOGGED_OUT")
    ) {
      console.log("clearing storage due to logged out");
      clearLocalStorage();
      window.location.reload();
    }
  }

  // Mutation
  const updateMutation = useMutation<
    unknown,
    unknown,
    { [key: string]: string | number | boolean }
  >({
    mutationFn: (formData: { [key: string]: string | number | boolean }) =>
      updateCompanyDirector(selectedItem?.id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["directors"] });
      toast({
        title: "ສຳເລັດ",
        description: "ແກ້ໄຂຂໍ້ມູນ ຄະນະອຳນວຍການ ສໍາເລັດແລ້ວ",
      });
      // navigate({ to: "..", replace: true });

      setEditTenureModal(false);
      setEditFormData({
        status: "",
        endDoc: "",
      });
    },
    onError: (error) => {
      console.log(error);
      toast({
        title: "ຫຼົ້ມເຫຼວ",
        description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່",
        variant: "destructive",
      });
    },
  });

  // Effects
  useEffect(() => {
    if (tenureStatusRes?.data?.length > 0 && !search.activeStatus) {
      navigate({
        search: {
          ...search, // preserve existing search params
          activeStatus: tenureStatusRes?.data[1]?.id,
          page: search.page || 1,
        },
        replace: true,
      });
    }
  }, [tenureStatusRes, navigate, search]);

  // Functions
  const handleTabChange = (value: string) => {
    navigate({
      search: {
        activeStatus: value,
        page: 1,
      },
      replace: true,
    });
  };

  // Add pagination handler
  const handlePageChange = (page: number) => {
    navigate({
      search: {
        ...search,
        page,
      },
      replace: true,
    } as NavigateOptions);
  };

  // Data
  if (tenureStatusLoading || directorsLoading || tenureLengthLoading) {
    return <TableSkeleton />;
  }

  const tenureStatus = tenureStatusRes?.data as {
    id: string;
    tenureStatus: string;
  }[];

  const directors = directorsRes?.data as CompanyDirector[];

  const tenureLength = tenureLengthRes?.data as {
    tenureLength: number;
  };

  // Const Calculate Tenure Status:
  // ... existing code ...

  const calculateTenureStatus = (deployDate: string) => {
    const currentDate = new Date();
    const deploymentDate = new Date(deployDate);

    // Calculate the difference in years
    const yearDifference =
      currentDate.getFullYear() - deploymentDate.getFullYear();
    const monthDifference = currentDate.getMonth() - deploymentDate.getMonth();
    // const dayDifference = currentDate.getDate() - deploymentDate.getDate();

    // Convert to total months for more accurate comparison
    const totalMonthsDifference = yearDifference * 12 + monthDifference;

    // Get tenure length from the response (default to 3 if not available)
    const maxTenureYears = tenureLength?.tenureLength || 3;

    // Convert tenure years to months
    const maxTenureMonths = maxTenureYears * 12;

    // If already passed the tenure length
    if (totalMonthsDifference >= maxTenureMonths) {
      // return "UNDEPLOYED";

      return (
        <Badge className="text-red-500 bg-[#DC26261A]">
          ຢູ່ໃນຊ່ວງປົດປະຈຳການ
        </Badge>
      );
    }

    // If in the last 3 months of tenure
    if (totalMonthsDifference >= maxTenureMonths - 3) {
      // return "SOON_UNDEPLOYED";

      return (
        <Badge className="text-[#CA8A04] bg-[#EAB3081A]">ໃກ້ຈະໝົດປະຈຳກັນ</Badge>
      );
    }

    // Otherwise, still actively deployed
    return (
      <Badge className="text-green-800 bg-[#16A34A1A]">ດຳເນີນການຢູ່</Badge>
    );
  };

  // ... existing code ...

  return (
    <div className="space-y-[24px] ">
      <div className="text-[18px]">
        <h2 className="font-semibold text-zinc-950">ຄະນະອຳນວຍການ</h2>
        <p className="text-zinc-500">ລາຍຊື່ຄະນະອຳນວຍການ</p>

        <Separator className="my-6" />
      </div>

      <div className="flex justify-end w-full">
        <Button
          variant={"secondary"}
          onClick={() =>
            navigate({
              to: "/account-management/soe/director/preview",
            })
          }
        >
          ສະແດງຕົວຢ່າງ
        </Button>
        {company_management.isWrite && (
          <Button
            onClick={() =>
              navigate({
                to: "/account-management/soe/director/mutate",
              } as NavigateOptions)
            }
          >
            <PlusCircle className="mr-2" />
            ເພີ່ມຂໍ້ມູນ
          </Button>
        )}
      </div>

      <Tabs
        value={search.activeStatus}
        onValueChange={(value) => handleTabChange(value)}
        className="flex flex-col gap-y-12"
      >
        <TabsList className="flex flex-wrap items-start justify-start gap-2 bg-transparent">
          {tenureStatus?.slice(1)?.map((status) => (
            <TabsTrigger
              key={status.id}
              value={status.id}
              className="px-3 bg-gray-100 rounded-full text-black data-[state=active]:bg-green-500 data-[state=active]:text-white"
            >
              {status.tenureStatus}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Table className="border rounded-md">
        <TableHeader className="">
          <TableRow>
            {tableData.headers.map((header) => (
              <TableHead className="whitespace-nowrap" key={header}>
                {header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {directors?.map((item, index) => (
            <TableRow key={item.id} className="cursor-pointer">
              <TableCell
                // 	onClick={() =>
                // 	navigate({
                // 		to: "/account-management/soe/director/$id",
                // 		params: { id: companyId, detailId: item.id },
                // 		search: {
                // 			id: item.id,
                // 			data: JSON.stringify(item),
                // 		},
                // 	})
                // }
                className="whitespace-nowrap"
              >
                {index + 1}
              </TableCell>
              <TableCell
                onClick={() =>
                  navigate({
                    to: "/account-management/soe/director/$id",
                    params: {
                      id: item.id,
                    },
                    search: {
                      id: item.id,
                      data: JSON.stringify(item),
                    },
                  })
                }
                className="whitespace-nowrap"
              >
                <Avatar>
                  <AvatarImage
                    className="object-cover"
                    src={S3_BUCKET_LINK + item?.profileUrl}
                  />
                  <AvatarFallback>{item?.firstName}</AvatarFallback>
                </Avatar>
              </TableCell>
              <TableCell
                onClick={() =>
                  navigate({
                    to: "/account-management/soe/director/$id",
                    params: {
                      id: item.id,
                    },
                    search: {
                      id: item.id,
                      data: JSON.stringify(item),
                    },
                  })
                }
                className="whitespace-nowrap"
              >
                {item?.gender === "MALE" ? "ທ່ານ" : "ທ່ານ ນາງ"}{" "}
                {item?.firstName} {item?.lastName}
              </TableCell>

              <TableCell
                onClick={() =>
                  navigate({
                    to: "/account-management/soe/director/$id",
                    params: {
                      id: item.id,
                    },
                    search: {
                      id: item.id,
                      data: JSON.stringify(item),
                    },
                  })
                }
                className="whitespace-nowrap"
              >
                {item?.position}
              </TableCell>
              <TableCell
                onClick={() =>
                  navigate({
                    to: "/account-management/soe/director/$id",
                    params: {
                      id: item.id,
                    },
                    search: {
                      id: item.id,
                      data: JSON.stringify(item),
                    },
                  })
                }
              >
                {item?.docFile
                  ? company_management.isDownload && (
                    // <a
                    //   download={S3_BUCKET_LINK + item?.docFile}
                    //   href={S3_BUCKET_LINK + item?.docFile}
                    //   target="_blank"
                    //   rel="noreferrer"
                    //   className="text-blue-500 underline"
                    // >
                    //   ເອກະສານຕິດຂັດ
                    // </a>
                    <FileHandleViewer
                      fileUrl={item?.docFile}
                      showBtnPreview={true}
                      text="ເອກະສານຕິດຂັດ"
                    />
                  )
                  : "-"}
              </TableCell>
              <TableCell
                onClick={() =>
                  navigate({
                    to: "/account-management/soe/director/$id",
                    params: {
                      id: item.id,
                    },
                    search: {
                      id: item.id,
                      data: JSON.stringify(item),
                    },
                  })
                }
                className="whitespace-nowrap"
              >
                {formatDate({
                  date: item?.agreementDate,
                  format: "DD/MM/YYYY",
                })}
              </TableCell>
              <TableCell
                onClick={() =>
                  navigate({
                    to: "/account-management/soe/director/$id",
                    params: {
                      id: item.id,
                    },
                    search: {
                      id: item.id,
                      data: JSON.stringify(item),
                    },
                  })
                }
                className="whitespace-nowrap"
              >
                {item?.publicStatus === "PENDING" ? (
                  <span className="text-yellow-500">ລໍຖ້າອະນຸມັດ</span>
                ) : item?.publicStatus === "REJECTED" ? (
                  <span
                    className="cursor-pointer text-red-500 hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedReason(item.rejectReason || "");
                      setReasonModalOpen(true);
                    }}
                  >
                    ຖືກປະຕິເສດ
                  </span>
                ) : item?.publicStatus === "APPROVED" ? (
                  calculateTenureStatus(item?.agreementDate)
                ) : (
                  "-"
                )}
              </TableCell>
              <TableCell>
                {company_management.isUpdate && (
                  <Button
                    onClick={() => {
                      setSelectedItem(item);
                      setEditTenureModal(true);
                      setEditFormData((prev) => ({
                        ...prev,
                        status: item?.tenureStatusId,
                        endDoc: item?.docFile || "",
                      }));
                    }}
                    variant={"outline"}
                  >
                    ອັບເດດສະຖານະ
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Pagination
        handlePageClick={handlePageChange}
        totalItems={directorsRes?.total || 0}
        itemsPerPage={40}
        currentPage={search.page}
      />

      <Dialog open={editTenureModal} onOpenChange={setEditTenureModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ຂໍ້ຕົກລົງ (ຄຜອ)</DialogTitle>
            <DialogDescription>
              ກາລຸນາກວດສອບຂໍ້ມູນຂອງທ່ານໃຫ້ຖືກຕ້ອງ ກ່ອນກົດບັນທຶກ
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();

              if (!editFormData?.endDoc) {
                toast({
                  title: "ຫຼົ້ມເຫຼວ",
                  description: "ກະລຸນາອັບໂຫຼດເອກະສານແຕ່ງຕັ້ງ",
                  variant: "destructive",
                });

                return;
              }

              const formData = new FormData(e.target as HTMLFormElement);

              const payload = {
                tenureStatusId: editFormData.status,
                endDoc: editFormData.endDoc,
                endNo: formData.get("endNo") as string,
                endDate: formData.get("endDate") as string,
                id: selectedItem.id,
              };

              console.log("Payload", payload);

              updateMutation.mutate(payload);
            }}
            className="grid gap-4 py-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col items-start gap-4">
                <Label htmlFor="agreementNo" className="text-right">
                  ເລກທີແຕ່ງຕັ້ງ
                </Label>
                <Input
                  readOnly
                  id="agreementNo"
                  name="agreementNo"
                  type="text"
                  defaultValue={selectedItem?.agreementNo}
                />
              </div>
              <div className="flex flex-col items-start gap-4">
                <Label htmlFor="agreementDate" className="text-right">
                  ວັນທີເດືອນປີແຕ່ງຕັ້ງ
                </Label>
                <Input
                  readOnly
                  id="agreementDate"
                  name="agreementDate"
                  type="date"
                  defaultValue={formatDate({
                    date: selectedItem?.agreementDate,
                    format: "YYYY-MM-DD",
                  })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="districtId">ສະຖານະ</Label>
              <Select
                name="tenureStatusId"
                onValueChange={(value) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    status: value,
                  }))
                }
                defaultValue={selectedItem?.tenureStatusId}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="ເລືອກເມືອງ" />
                </SelectTrigger>
                <SelectContent>
                  {tenureStatus?.map((item) => (
                    <SelectItem value={item.id} key={item.id}>
                      {item?.tenureStatus}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col items-start gap-4">
                <Label htmlFor="endNo" className="text-right">
                  ເລກທີສິ້ນສຸດ <span className="text-red-500">*</span>
                </Label>
                <Input
                  required
                  id="endNo"
                  name="endNo"
                  type="text"
                  placeholder="ປ້ອນເລກທີສິ້ນສຸດ..."
                  defaultValue={selectedItem?.agreementNo}
                />
              </div>
              <div className="flex flex-col items-start gap-4">
                <Label htmlFor="endDate" className="text-right">
                  ວັນທີເດືອນປີສິ້ນສຸດ <span className="text-red-500">*</span>
                </Label>
                <Input
                  required
                  id="endDate"
                  name="endDate"
                  type="date"
                  defaultValue={formatDate({
                    date: selectedItem?.endDate || "",
                    format: "YYYY-MM-DD",
                  })}
                />
              </div>
            </div>

            <div>
              <FileUpload
                required={true}
                currentFile={
                  editFormData?.endDoc || selectedItem?.docFile || ""
                }
                onFileUpload={(file) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    endDoc: file,
                  }))
                }
              />
            </div>

            <DialogFooter>
              <DialogClose>
                <Button variant={"outline"} type="button">
                  ຍົກເລີກ
                </Button>
              </DialogClose>
              <Button type="submit">
                {updateMutation.isPending ? (
                  <Spinner className="text-white" />
                ) : (
                  <>
                    <Save className="mr-2" />
                    ບັນທຶກຂໍ້ມູນ
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <ReasonModal
        open={reasonModalOpen}
        onOpenChange={setReasonModalOpen}
        reason={selectedReason}
      />
    </div >
  );
}

const tableData = {
  headers: [
    "ລຳດັບ",
    "ຮູບໂປຣຟາຍ",
    "ຊື່ ແລະ ນາມສະກຸນ",
    "ຕຳແໜ່ງພາຍໃນວິສາຫະກິດ",
    "ເອກະສານຕິດຄັດ",
    "ວັນທີແຕ່ງຕັ້ງ",
    "ສະຖານະ",
    "ອັບເດດສະຖານະ",
  ],
};
